import z from 'zod';
import { MessageCircle, Calendar, Search, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

import { useToken } from '@/context/useToken';
import { cn } from '@/utils';
import { addSearchParam, useSearchParam } from '@/helpers/url';
import { useDebouncedFormSubmit } from '@/helpers/forms';

import LoginRequired from '@/components/common/LoginRequired';
import Page from '@/components/layout/Page';
import { Form } from '@/components/ui/form';
import Input from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import PageLoading from '@/components/layout/PageLoading';

import Conversations from './components/list/Conversations';
import { CONVERSATION_TYPE } from './enums';

import { useSelf } from '@/data/user';

const searchSchema = z.object({
  q: z.string().optional()
});

const ConversationList = () => {
  const self = useSelf();
  const navigate = useNavigate();
  const location = useLocation();
  const conversationType = useSearchParam('type');
  const searchQuery = useSearchParam('q');

  const form = useForm({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      q: searchQuery || ''
    }
  });

  useEffect(() => {
    form.setValue('q', searchQuery || '');
  }, [form, searchQuery]);

  const handleConversationTypeChange = (conversationType) => {
    const newParams = addSearchParam('type', conversationType);
    navigate(`${location.pathname}?${newParams}`);
  };

  const onSubmit = async (values) => {
    const searchParams = new URLSearchParams(location.search);

    if (values.q) {
      searchParams.set('q', values.q);
    } else {
      searchParams.delete('q');
    }

    if (conversationType) {
      searchParams.set('type', conversationType);
    }

    const queryString = searchParams.toString();
    navigate(`${location.pathname}${queryString ? `?${queryString}` : ''}`);
  };

  useDebouncedFormSubmit(form, 'q', onSubmit, 250);

  if (self.isLoading && !self.data) {
    return (
      <Page>
        <div className="flex justify-center items-center h-[calc(100vh-180px)]">
          <PageLoading />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Card className="h-full flex flex-col shadow-lg rounded-lg overflow-hidden">
        <div className="p-4 border-b">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <Input
                name="q"
                placeholder="Search conversations..."
                startIcon={<Search />}
                className="m-0 p-0"
                {...(form.watch('q')?.length > 0 && {
                  endIcon: <X />,
                  onEndIconClick: () => form.setValue('q', '')
                })}
              />
            </form>
          </Form>
        </div>

        <div className="flex items-center justify-center space-x-4 p-4 border-b">
          <Button
            variant="outline"
            className={cn(
              'w-1/2 transition-transform duration-300',
              conversationType === CONVERSATION_TYPE.DIRECT_MESSAGE &&
                'border-2 bg-muted text-white transform scale-105 shadow-lg',
              'hover:bg-muted'
            )}
            onClick={() =>
              handleConversationTypeChange(CONVERSATION_TYPE.DIRECT_MESSAGE)
            }
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Direct Messages
          </Button>
          <Button
            variant="outline"
            className={cn(
              'w-1/2 transition-transform duration-300',
              conversationType === CONVERSATION_TYPE.BOOKING &&
                'border-2 bg-muted text-white transform scale-105 shadow-lg',
              'hover:bg-muted'
            )}
            onClick={() =>
              handleConversationTypeChange(CONVERSATION_TYPE.BOOKING)
            }
          >
            <Calendar className="mr-2 h-4 w-4" />
            Bookings
          </Button>
        </div>
        <Conversations />
      </Card>
    </Page>
  );
};

const ConversationListLoader = () => {
  const { token } = useToken();

  if (!token) {
    return (
      <Page>
        <div className="flex justify-center items-center h-screen">
          <div className="text-center">
            <LoginRequired
              title="Inbox"
              message="You can view your messages once you've logged in."
            />
          </div>
        </div>
      </Page>
    );
  }

  return <ConversationList />;
};

export default ConversationListLoader;
